import type { SVGProps } from 'react';

export type SicaProductLogoLockup = 'horizontal' | 'stacked' | 'symbol';
export type SicaProductLogoTone = 'standard' | 'inverse';

export interface SicaProductLogoProps extends Omit<SVGProps<SVGSVGElement>, 'color'> {
  lockup?: SicaProductLogoLockup;
  tone?: SicaProductLogoTone;
  label?: string;
  decorative?: boolean;
}

// Official RVD.AI master geometry. Product lockups append a subordinate
// descriptor; they never redraw or separate the master wordmark.
const RVD_PATH =
  'M123.7 15q-1.95-4.5-5.4-7.8t-8.1-5.1C107.1.9 103.5.2 99.3.2H88.6c-1.8 0-3.5.7-4.9 2.2q-1.8 1.8-2.1 3.9c0-.9-.3-1.7-.7-2.4-.9-1.5-2.1-2.6-3.8-3.3Q74.4-.45 72 .6c-1.7.6-2.9 2-3.7 4l-8.5 21.7L52 6.2l-.6-1.6c0-.2-.2-.4-.2-.6L49.7.1h-4.4c-.8 0-1.7.2-2.6.5-1.7.7-2.9 1.8-3.8 3.3s-.9 3.3-.2 5.4l13.4 32.6 3.1 7.5h5.1c2.8 0 4.9-1.5 6.2-4.5L81.2 9.4c.2-.6.4-1.2.4-1.7v35c0 2.2.7 3.9 2.1 5 .3.3.6.5.8.7s.6.3.8.4c.9.5 2 .7 3.4.7h11.8c4.2-.2 7.5-.8 9.9-1.9 2.2-1 4.3-2.2 6.2-3.8s3.5-3.2 4.8-5.2 2.4-4.1 3.2-6.5q1.2-3.45 1.2-7.5c0-4.05-.7-6.7-2-9.7Zm-12.8 14.2c-.6 1.4-1.4 2.5-2.4 3.5s-2.2 1.7-3.6 2.3c-1.4.5-2.8.8-4.4.8h-5.3V14h6.8c1 0 2.1.3 3 .7 2.1.8 3.8 2.1 5 3.8 1.3 1.7 1.9 3.8 1.9 6.3s-.3 3.1-.9 4.4Z';
const R_PATH =
  'M24.1.2H0V7c.1 4.5 2.3 6.8 6.6 6.8h18.3q2.7 0 2.7 2.1c0 2.1-.9 2.1-2.7 2.1H7.7C2.6 18 .1 20.7.1 26.1s1.8 4.2 5.4 7l14.1 14 5.3 5.3 4.4-4.4.6-.6c0-.2.1-.2.1-.2 2.8-3.3 2.5-6.6-.7-10l-5.9-5.6h1.4c9.7-.3 14.5-5.5 14.5-15.7S34.3.2 24.1.2';
const AI_PATH =
  'M174.9 0c-3.9 0-7.1 3.2-7.1 7.1V41c0-.3-.1-.5-.2-.8L153 4.7c-1.3-3-3.4-4.5-6.2-4.5h-5.1l-3.1 7.5L128.2 33c.9-.2 1.8-.4 2.8-.4 5.4 0 9.9 3.8 10.9 9h11.6l1.4 3.5c.8 2.1 2 3.4 3.7 4s3.4.6 5.1 0c1.6-.7 2.9-1.8 3.8-3.3.3-.5.5-1.1.6-1.7.7 3.1 3.5 5.5 6.9 5.5 3.9 0 7.1-3.2 7.1-7V7.1c-.1-3.9-3.3-7.1-7.2-7.1m-31.5 30.9 3-7.7 3 7.7z';
const DOT_PATH = 'M131 38c-3.1 0-5.7 2.5-5.7 5.7s2.6 5.7 5.7 5.7 5.7-2.5 5.7-5.7-2.6-5.7-5.7-5.7';

function MasterWordmark({ primary, accent }: { primary: string; accent: string }) {
  return (
    <>
      <path d={RVD_PATH} fill={primary} />
      <path d={R_PATH} fill={primary} />
      <path d={AI_PATH} fill={accent} />
      <path d={DOT_PATH} fill={accent} />
    </>
  );
}

function Descriptor({ x, y, ink, accent }: { x: number; y: number; ink: string; accent: string }) {
  return (
    <>
      <text
        x={x}
        y={y}
        fill={ink}
        fontFamily="Arial, Helvetica, sans-serif"
        fontSize="18"
        fontWeight="700"
        letterSpacing="1.7"
      >
        SICA
      </text>
      <path
        d={`M${x} ${y + 10.5}h61`}
        fill="none"
        stroke={accent}
        strokeLinecap="round"
        strokeWidth="2.4"
      />
    </>
  );
}

export function SicaProductLogo({
  lockup = 'horizontal',
  tone = 'standard',
  label = 'RVD.AI SICA',
  decorative = false,
  ...props
}: SicaProductLogoProps) {
  // The mark's colours come from `@rvd-ai/brand` (vendored in `../vendor/brand`),
  // never from a literal here: `--rvd-logo-primary` was a variable no package ever
  // defined, so every render fell through to a transcribed hex.
  const primary = tone === 'inverse' ? '#FFFFFF' : 'var(--rvd-brand-navy)';
  /** What sits *on* the primary fill — the inverse of it, for the enclosed symbol. */
  const onPrimary = tone === 'inverse' ? 'var(--rvd-brand-navy)' : '#FFFFFF';
  const accent = 'var(--rvd-brand-cyan)';
  const accessibleProps = decorative
    ? ({ 'aria-hidden': true } as const)
    : ({ 'aria-label': label, role: 'img' } as const);

  if (lockup === 'symbol') {
    return (
      <svg
        data-product-logo-lockup="symbol"
        data-product-logo-tone={tone}
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        {...accessibleProps}
        {...props}
      >
        <rect width="64" height="64" rx="14" fill={primary} />
        <g transform="translate(12 6) scale(1.05)">
          <path d={R_PATH} fill={onPrimary} />
        </g>
        <path d="M13 55h38" stroke={accent} strokeLinecap="round" strokeWidth="2.6" />
      </svg>
    );
  }

  if (lockup === 'stacked') {
    return (
      <svg
        data-product-logo-lockup="stacked"
        data-product-logo-tone={tone}
        viewBox="0 0 214 88"
        xmlns="http://www.w3.org/2000/svg"
        {...accessibleProps}
        {...props}
      >
        <g transform="translate(16 0)">
          <MasterWordmark primary={primary} accent={accent} />
        </g>
        <Descriptor x={76} y={73} ink={primary} accent={accent} />
      </svg>
    );
  }

  return (
    <svg
      data-product-logo-lockup="horizontal"
      data-product-logo-tone={tone}
      viewBox="0 0 282 56"
      xmlns="http://www.w3.org/2000/svg"
      {...accessibleProps}
      {...props}
    >
      <MasterWordmark primary={primary} accent={accent} />
      <path d="M197 7v39" stroke={accent} strokeLinecap="round" strokeWidth="2.4" />
      <Descriptor x={211} y={31} ink={primary} accent={accent} />
    </svg>
  );
}
